/**
 * The journey (decision 0031, Daniel's idea and his name for it, 2026-10-27): once a run has ended
 * at a creature and the result sheet is up, the world above the sheet shows how the expedition
 * went. The camera rises to look down on all the run revealed, the mist closes over it, and the
 * run's own actions open it again in their order, in about five seconds whatever the run's
 * length, until the chasm opens and its creature rises once more. Purely visual: the run, its
 * record and all that is kept are untouched, and the world after it is the world before it.
 */
import {
  type Action,
  type ActionResult,
  type Coord,
  Game,
  type RunRecord,
} from "@mistsweeper/core";
import {
  ART_PER_TILE,
  type CameraView,
  MAX_PITCH,
  type WorldRenderer,
} from "@mistsweeper/renderer";

/** About how long the ground takes to open again, whatever the run's length. */
export const UNFOLD_MS = 5000;
/** The slowest and the fastest one step may follow the one before. */
const SLOWEST_MS = 350;
const FASTEST_MS = 40;
/** The camera's rise to look down on the run. */
const RISE_MS = 1200;
/**
 * The mist closing over the run as the creatures sink back into their gaps (a banner taken away
 * is gone in 0.7 seconds), then a breath under the mist before the first step.
 */
const CLOSE_MS = 750;
const BREATH_MS = 250;
/** Tiles of margin round what the run revealed, in the view from above. */
const MARGIN = 1;

/** The rhythm of a journey's steps: a tick every `interval` milliseconds, `perTick` steps each. */
export interface Pace {
  readonly interval: number;
  readonly perTick: number;
}

/**
 * The rhythm of n steps: one every 5000 / n milliseconds, kept between 40 and 350; past 125 steps
 * several a tick, so a long run still opens in about five seconds.
 */
export function journeyPace(n: number): Pace {
  if (n * FASTEST_MS <= UNFOLD_MS) {
    return { interval: Math.min(SLOWEST_MS, UNFOLD_MS / Math.max(1, n)), perTick: 1 };
  }
  const perTick = Math.ceil((n * FASTEST_MS) / UNFOLD_MS);
  return { interval: UNFOLD_MS / Math.ceil(n / perTick), perTick };
}

/** Takes one action of a record on a game, as `Game.fromRecord` does. */
export function applyAction(game: Game, action: Action): ActionResult {
  return game.apply(action);
}

/** A run at its start, the cabin's opening reveal alone: where its journey sets out from. */
export function journeyStart(record: RunRecord): Game {
  return Game.fromRecord({ ...record, actions: [] });
}

/** The actions of a run that changed something, in their order: its journey's steps. */
export function journeySteps(record: RunRecord): Action[] {
  const game = journeyStart(record);
  return record.actions.filter((action) => applyAction(game, action).changed);
}

/**
 * The tiles a run shows that its start does not: what it revealed and the ring round it, which
 * shows after death and holds its banners and chasms. The mist closes over these.
 */
export function journeyGround(game: Game, start: Game): Coord[] {
  const seen = new Set<string>();
  const ground: Coord[] = [];
  for (const t of game.revealedTiles()) {
    for (let dy = -1; dy <= 1; dy += 1) {
      for (let dx = -1; dx <= 1; dx += 1) {
        const x = t.x + dx;
        const y = t.y + dy;
        const key = `${x},${y}`;
        if (seen.has(key) || start.isRevealed(x, y)) continue;
        seen.add(key);
        ground.push({ x, y });
      }
    }
  }
  return ground;
}

/** The screen in device pixels, and how much of its top and its foot the world does not show in. */
export interface Area {
  readonly w: number;
  readonly h: number;
  readonly top: number;
  readonly bottom: number;
}

/**
 * Where the camera looks down on a journey from, turned by `yaw` and tilted by `pitch`: the point
 * and the scale, in device pixels per art unit, that fit every tile, with a tile's margin round
 * it, into the part of the screen the world shows in, as the camera of the visual spec projects
 * the ground. The scale stays within `min` and `max`, so a long run may not fit.
 */
export function journeyFrame(
  tiles: readonly Coord[],
  yaw: number,
  pitch: number,
  area: Area,
  min: number,
  max: number,
): { x: number; y: number; scale: number } {
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  const lift = Math.sin(pitch);
  // Across the screen and down it, in tiles: the ground under the orthographic camera.
  let u0 = Number.POSITIVE_INFINITY;
  let u1 = Number.NEGATIVE_INFINITY;
  let v0 = Number.POSITIVE_INFINITY;
  let v1 = Number.NEGATIVE_INFINITY;
  const r = 0.5 + MARGIN;
  for (const t of tiles.length > 0 ? tiles : [{ x: 0, y: 0 }]) {
    for (const [dx, dy] of [
      [-r, -r],
      [r, -r],
      [-r, r],
      [r, r],
    ] as const) {
      const x = t.x + dx;
      const y = t.y + dy;
      const u = x * c - y * s;
      const v = lift * (x * s + y * c);
      u0 = Math.min(u0, u);
      u1 = Math.max(u1, u);
      v0 = Math.min(v0, v);
      v1 = Math.max(v1, v);
    }
  }
  const high = Math.max(1, area.h - area.top - area.bottom);
  const fit = Math.min(area.w / (u1 - u0), high / (v1 - v0)) / ART_PER_TILE;
  const scale = Math.min(max, Math.max(min, fit));
  const k = scale * ART_PER_TILE;
  // The middle of what was revealed in the middle of the area, which sits above the screen's.
  const u = (u0 + u1) / 2;
  const v = (v0 + v1) / 2 - (area.top + high / 2 - area.h / 2) / k;
  const q = v / lift;
  return { x: u * c + q * s, y: -u * s + q * c, scale };
}

/**
 * The camera's view a share `e` of the way from one view to another, the turn kept: the point and
 * the tilt in a straight line, the scale by the same factor each step, so the zoom feels even.
 */
export function viewBetween(
  from: CameraView,
  to: { readonly x: number; readonly y: number; readonly pitch: number; readonly scale: number },
  e: number,
): CameraView {
  return {
    x: from.x + (to.x - from.x) * e,
    y: from.y + (to.y - from.y) * e,
    yaw: from.yaw,
    pitch: from.pitch + (to.pitch - from.pitch) * e,
    scale: from.scale * (to.scale / from.scale) ** e,
  };
}

/** What the journey asks of the game round it. */
export interface JourneyHost {
  readonly view: WorldRenderer;
  /** The run that ended, as it stands. */
  game(): Game;
  /** The part of the screen the world shows in: above the result sheet. */
  area(): Area;
  /**
   * The scales the view from above keeps within, in device pixels per art unit: the camera's
   * farthest, and no closer than a run starts, so a short run is not looked at from up close.
   */
  scales(): { readonly min: number; readonly max: number };
  /** Whether a journey may play now: the run that shows has ended and nothing is in the way. */
  ready(): boolean;
  /** Whether the result sheet is up. */
  sheetUp(): boolean;
  /** Calls `frame` with the share of `ms` gone, eased by the caller's clock, then `done`. */
  ease(ms: number, frame: (p: number) => void, done: () => void): void;
  /** Stops the camera's eased motion. */
  stop(): void;
  /** Asks for a frame. */
  frame(): void;
}

/** Where a journey is: waiting to set out, the camera rising, the mist closing, unfolding, or ended. */
export type Phase = "idle" | "waiting" | "rising" | "closing" | "unfolding" | "ended";

/** One run's journey, played after its death and again on a tap on the world. */
export class Journey {
  private readonly host: JourneyHost;
  private phase: Phase = "idle";
  private timer: ReturnType<typeof setTimeout> | null = null;
  private record: RunRecord | null = null;
  /** The run opened again step by step, drawn instead of the run's own while it unfolds. */
  private shown: Game | null = null;
  /** Where the camera looked before the journey moved it, which is what the run's view keeps. */
  private before: CameraView | null = null;

  constructor(host: JourneyHost) {
    this.host = host;
  }

  /** The world to draw instead of the run's own, while the journey unfolds. */
  get world(): Game | null {
    return this.shown;
  }

  /** The view to keep for the run that shows instead of where the journey left the camera. */
  get keptView(): CameraView | null {
    return this.before;
  }

  /** Where it is: waiting to set out, the camera rising, the mist closing, unfolding, or ended. */
  get state(): Phase {
    return this.phase;
  }

  /** Whether it moves the camera or the mist now, so a touch only stops it. */
  get playing(): boolean {
    return this.phase === "rising" || this.phase === "closing" || this.phase === "unfolding";
  }

  /** A run has ended and its result is up: its journey sets out after `delay` milliseconds. */
  begin(delay: number): void {
    if (this.playing) this.finish();
    this.clearTimer();
    this.before = null;
    this.record = this.host.game().record;
    this.phase = "waiting";
    this.timer = setTimeout(() => this.rise(), delay);
  }

  /** After it ended, or was stopped before it began: plays it again above the sheet. Whether it did. */
  again(): boolean {
    if (this.phase !== "ended" || !this.record) return false;
    if (!this.host.ready() || !this.host.sheetUp()) return false;
    this.rise();
    return true;
  }

  /**
   * A touch, a click, a key or the menu: the journey shows its end at once; or, still waiting to
   * set out, it does not. Whether it was playing, in which case that is all the touch does.
   */
  interrupt(): boolean {
    if (this.phase === "waiting") {
      this.clearTimer();
      this.phase = "ended";
      return false;
    }
    if (!this.playing) return false;
    this.finish();
    return true;
  }

  /**
   * A new run or another place: the journey ends, and the camera goes back to where it looked
   * before it, so the views keep what the player chose.
   */
  leave(): void {
    if (this.playing) this.finish();
    this.clearTimer();
    if (this.before) this.host.view.setCameraView(this.before);
    this.before = null;
    this.record = null;
    this.phase = "idle";
  }

  private rise(): void {
    this.timer = null;
    const record = this.record;
    // It plays above the sheet, and only while the run that ended is the one that shows.
    if (!record || !this.host.ready() || !this.host.sheetUp()) {
      this.phase = "ended";
      return;
    }
    this.phase = "rising";
    const view = this.host.view;
    const from = view.cameraView();
    this.before ??= from;
    const { min, max } = this.host.scales();
    const to = journeyFrame(
      this.host.game().revealedTiles(),
      from.yaw,
      MAX_PITCH,
      this.host.area(),
      min,
      max,
    );
    this.glide(from, { ...to, pitch: MAX_PITCH }, RISE_MS, () => this.close(record));
  }

  /** Glides the camera between two views over `ms`, eased out as its glide to a place is. */
  private glide(
    from: CameraView,
    to: { readonly x: number; readonly y: number; readonly pitch: number; readonly scale: number },
    ms: number,
    done: () => void,
  ): void {
    const view = this.host.view;
    this.host.ease(
      ms,
      (p) => view.setCameraView(viewBetween(from, to, 1 - (1 - p) ** 3)),
      () => {
        view.settle();
        done();
      },
    );
  }

  private close(record: RunRecord): void {
    if (this.phase !== "rising") return;
    this.phase = "closing";
    const now = performance.now();
    const view = this.host.view;
    view.fog.close(journeyGround(this.host.game(), journeyStart(record)), now);
    view.lowerCreatures(now);
    this.host.frame();
    this.timer = setTimeout(() => this.unfold(record), CLOSE_MS);
  }

  private unfold(record: RunRecord): void {
    this.timer = null;
    if (this.phase !== "closing") return;
    this.phase = "unfolding";
    const view = this.host.view;
    const game = journeyStart(record);
    const steps = journeySteps(record);
    this.shown = game;
    view.fog.open();
    view.forgetCreatures();
    this.host.frame();
    const pace = journeyPace(steps.length);
    const t0 = performance.now() + BREATH_MS;
    let taken = 0;
    const tick = () => {
      this.timer = null;
      if (this.phase !== "unfolding") return;
      const now = performance.now();
      // Every step due by now, so a late timer does not slow the journey.
      const ticks = Math.floor((now - t0) / pace.interval) + 1;
      const due = Math.min(steps.length, ticks * pace.perTick);
      for (; taken < due; taken += 1) {
        const step = steps[taken];
        if (!step) break;
        const result = applyAction(game, step);
        if (result.revealed.length > 0) view.fog.markRevealed(result.revealed, now);
      }
      view.invalidate();
      this.host.frame();
      if (taken >= steps.length) {
        this.end();
        return;
      }
      this.timer = setTimeout(tick, Math.max(0, t0 + ticks * pace.interval - now));
    };
    this.timer = setTimeout(tick, BREATH_MS);
  }

  /** The last step taken: the run's own world shows again, the same as the journey's now. */
  private end(): void {
    this.shown = null;
    this.phase = "ended";
    this.host.frame();
  }

  /** Shows the end at once: the run as it ended, its creatures risen and banners unrolled. */
  private finish(): void {
    this.clearTimer();
    this.host.stop();
    const view = this.host.view;
    view.fog.open();
    // Drawn as a run opened again, the creatures stand risen and the banners unrolled.
    view.startRun();
    view.invalidate();
    this.shown = null;
    this.phase = "ended";
    this.host.frame();
  }

  private clearTimer(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }
}
